import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import * as v from "valibot";
import { getLoginSchema } from "@/lib/schemas/auth";
import { requestMagicLink } from "@/store/auth";
import { localized, msg } from "@lit/localize";
import { extractAuthError } from "./errors";
import "../../components/ui/field.ts";
import "../../components/ui/input.ts";
import "../../components/ui/button.ts";
import "../../components/ui/spinner.ts";
import "../../components/ui/iris-icon.ts";
import "../../layouts/auth-layout.ts";

@localized()
@customElement("iris-login-page")
export class IrisLoginPage extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }
    .panel {
      width: 100%;
      max-width: 400px;
      padding: var(--space-8) var(--space-7);
      border: 1px solid var(--border);
      border-radius: var(--radius-xl);
      background: var(--card);
      box-shadow:
        0 1px 2px rgba(15, 23, 42, 0.04),
        0 24px 48px -24px rgba(15, 23, 42, 0.18);
      animation: card-in var(--dur-glacial) var(--ease-1) both;
    }
    @keyframes card-in {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.985);
      }
      to {
        opacity: 1;
        transform: none;
      }
    }
    /* Children arrive one after another, timed off the card's own entrance. */
    .panel > * {
      animation: rise var(--dur-glacial) var(--ease-1) both;
      animation-delay: calc(
        (var(--dur-glacial) * 0.4) + var(--i, 0) * 70ms * var(--motion-scale)
      );
    }
    @keyframes rise {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: none;
      }
    }

    .eyebrow {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1-5);
      margin: 0 0 var(--space-3);
      padding: var(--space-1) var(--space-2-5);
      border-radius: var(--radius-full);
      background: color-mix(in srgb, var(--primary) 12%, transparent);
      color: var(--primary);
      font-size: var(--text-xs);
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    h1 {
      margin: 0 0 var(--space-1-5);
      font-size: var(--text-2xl);
      font-weight: 600;
      letter-spacing: -0.01em;
      color: var(--foreground);
    }
    .sub {
      margin: 0 0 var(--space-6);
      font-size: var(--text-sm);
      line-height: 1.55;
      color: var(--muted-foreground);
    }
    form {
      display: flex;
      flex-direction: column;
      gap: var(--space-4);
    }
    form > * {
      animation: rise var(--dur-glacial) var(--ease-1) both;
      animation-delay: calc(
        (var(--dur-glacial) * 0.4) + var(--i, 0) * 70ms * var(--motion-scale)
      );
    }
    .form-error {
      display: flex;
      align-items: center;
      gap: var(--space-1-5);
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-md);
      background: color-mix(in srgb, var(--destructive) 10%, transparent);
      color: var(--destructive);
      font-size: var(--text-xs);
      font-weight: 500;
    }
    .shake {
      animation: shake var(--dur-glacial) var(--ease-2);
    }
    @keyframes shake {
      10%,
      90% {
        transform: translateX(-1px);
      }
      20%,
      80% {
        transform: translateX(2px);
      }
      30%,
      50%,
      70% {
        transform: translateX(-4px);
      }
      40%,
      60% {
        transform: translateX(4px);
      }
    }

    /* Submit: the arrow slides on hover so the button reads as "go". */
    iris-button {
      margin-top: var(--space-1);
    }
    .cta {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
    }
    .cta iris-icon {
      transition: transform var(--dur-fast) var(--ease-2);
    }
    iris-button:hover .cta iris-icon {
      transform: translateX(3px);
    }

    /* Hint text matches the muted helper size used across the app. */
    .hint {
      margin: 0;
      text-align: center;
      font-size: var(--text-xs);
      line-height: 1.5;
      color: var(--muted-foreground);
    }
    .hint a {
      color: var(--primary);
      font-weight: 500;
      text-decoration: none;
    }
    .hint a:hover {
      text-decoration: underline;
    }

    /* ── Sent state ────────────────────────────────────────────── */
    .sent {
      text-align: center;
    }
    .sent-icon {
      display: grid;
      place-items: center;
      width: 64px;
      height: 64px;
      margin: 0 auto var(--space-5);
      border-radius: var(--radius-full);
      background: color-mix(in srgb, var(--primary) 12%, transparent);
      color: var(--primary);
      animation: pop var(--dur-glacial) var(--ease-1) both;
    }
    @keyframes pop {
      from {
        opacity: 0;
        transform: scale(0.6);
      }
      60% {
        transform: scale(1.08);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    .sent .sub {
      margin-bottom: var(--space-4);
    }
    .chip {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: var(--space-5);
      padding: var(--space-1-5) var(--space-3);
      border-radius: var(--radius-full);
      border: 1px solid var(--border);
      background: var(--muted);
      color: var(--foreground);
      font-size: var(--text-sm);
      font-weight: 500;
    }
  `;

  @state()
  email = "";
  @state()
  errors: Partial<Record<"email" | "form", string>> = {};
  @state()
  submitting = false;
  /** Set once a link has been requested, switching to the confirmation view. */
  @state()
  linkSent = false;

  protected render() {
    return html`
      <iris-auth-layout>
        <section class="panel" aria-live="polite">
          ${this.linkSent ? this.#renderLinkSent() : this.#renderForm()}
        </section>
      </iris-auth-layout>
    `;
  }

  #renderForm() {
    return html`
      <span class="eyebrow" style="--i:0">
        <iris-icon name="mail" size="12"></iris-icon>
        ${msg("Passwordless")}
      </span>
      <h1 style="--i:1">${msg("Welcome back")}</h1>
      <p class="sub" style="--i:2">
        ${msg(
          "Enter your work email and we'll send you a one-time sign-in link.",
        )}
      </p>
      <form
        @submit="${this.#onSubmit}"
        novalidate
        class="${this.errors.form ? "shake" : ""}"
        style="--i:3"
      >
        <iris-field
          style="--i:0"
          label=${msg("Email")}
          .error="${this.errors.email ?? ""}"
          ?invalid="${!!this.errors.email}"
        >
          <iris-input
            id="email"
            name="email"
            type="email"
            autocomplete
            autofocus
            placeholder=${msg("you@company.com")}
            .value="${live(this.email)}"
            @input="${(
              e: Event,
            ) => (this.email = (e.target as HTMLInputElement).value)}"
            ?invalid="${!!this.errors.email}"
          ></iris-input>
        </iris-field>
        ${this.errors.form
          ? html`
            <div class="form-error" role="alert" style="--i:1">
              <iris-icon name="alert-circle" size="14"></iris-icon>
              ${this.errors.form}
            </div>
          `
          : null}
        <iris-button
          type="submit"
          fluid
          ?disabled="${this.submitting}"
          style="--i:2"
        >
          ${this.submitting
            ? html`
              <span class="cta">
                <iris-spinner></iris-spinner>
                ${msg("Sending link…")}
              </span>
            `
            : html`
              <span class="cta">
                ${msg("Continue with email")}
                <iris-icon name="arrow-right" size="16"></iris-icon>
              </span>
            `}
        </iris-button>
        <p class="hint" style="--i:3">
          ${msg("No password needed — the link signs you in securely.")}
        </p>
      </form>
    `;
  }

  /**
   * Shown after a link is requested. The wording avoids confirming that the
   * address exists, mirroring the backend's deliberately uniform response.
   */
  #renderLinkSent() {
    return html`
      <div class="sent">
        <div class="sent-icon" style="--i:0">
          <iris-icon name="check-circle" size="28"></iris-icon>
        </div>
        <h1 style="--i:1">${msg("Check your inbox")}</h1>
        <p class="sub" style="--i:2">
          ${msg("If that address can sign in, a link is on its way to")}
        </p>
        <span class="chip" style="--i:3">${this.email}</span>
        <p class="hint" style="--i:4">
          ${msg("The link expires shortly.")}
          <a
            href="/login"
            @click=${(e: MouseEvent) => {
              e.preventDefault();
              this.linkSent = false;
            }}
          >${msg("Use a different email")}</a>
        </p>
      </div>
    `;
  }

  #onSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    this.errors = {};
    const result = v.safeParse(getLoginSchema(), { email: this.email });
    if (!result.success) {
      const issues = result.issues.reduce<Record<string, string>>(
        (acc, issue) => {
          const key = issue.path?.[0]?.key ?? "form";
          acc[key as string] = issue.message;
          return acc;
        },
        {},
      );
      this.errors = issues as Partial<Record<"email" | "form", string>>;
      return;
    }
    this.submitting = true;
    try {
      await requestMagicLink(result.output.email);
      // The backend answers identically whether or not the address may sign
      // in, so this confirmation is deliberately non-committal.
      this.linkSent = true;
    } catch (err) {
      this.errors = {
        ...this.errors,
        form: extractAuthError(err),
      };
    } finally {
      this.submitting = false;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "iris-login-page": IrisLoginPage;
  }
}
